import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { FlamingoError } from './flamingo-error.handler';

@Injectable()
export class FlamingoHttpService {
  private commonHeaders: HttpHeaders;
  private baseUrl = environment.baseUrl;

  constructor(private http: HttpClient) {
    this.commonHeaders = new HttpHeaders().set('Content-Type', 'application/json');
  }

  public get(_url: string, params?: HttpParams): Observable<any> {
    const url = this.baseUrl + _url;
    return this.http
      .get(url, {
        headers: this.commonHeaders,
        params: params,
        responseType: 'json',
        withCredentials: true,
      })
      .pipe(catchError(this.handleError));
  }

  public getUrl(_url: string, params?: HttpParams): Observable<any> {
    const url = this.baseUrl + _url;
    return this.http
      .get(url, {
        headers: this.commonHeaders,
        params: params,
        responseType: 'text',
        withCredentials: true,
      })
      .pipe(catchError(this.handleError));
  }

  public post(_url: string, _body?: any): Observable<any> {
    const url = this.baseUrl + _url;
    let body = _body;

    if (!body) {
      body = {};
    }

    return this.http
      .post(url, body, {
        headers: this.commonHeaders,
        responseType: 'json',
        withCredentials: true,
      })
      .pipe(catchError(this.handleError));
  }

  public postWithParams(_url: string, params?: HttpParams, _body?: any): Observable<any> {
    const url = this.baseUrl + _url;
    let body = _body;

    if (!body) {
      body = {};
    }

    return this.http
      .post(url, body, {
        headers: this.commonHeaders,
        params: params,
        responseType: 'json',
        withCredentials: true,
      })
      .pipe(catchError(this.handleError));
  }

  public postFormData(_url: string, formData: FormData): Observable<any> {
    const url = this.baseUrl + _url;

    return this.http.post(url, formData, {
      responseType: 'json',
      withCredentials: true,
    });
  }

  public put(_url: string, _body?: any): Observable<any> {
    const url = this.baseUrl + _url;
    let body = _body;

    if (!body) {
      body = {};
    }

    return this.http
      .put(url, body, { headers: this.commonHeaders, responseType: 'json', withCredentials: true })
      .pipe(catchError(this.handleError));
  }

  public putFormData(_url: string, formData: FormData): Observable<any> {
    const url = this.baseUrl + _url;

    return this.http.put(url, formData, {
      responseType: 'json',
      withCredentials: true,
    });
  }

  public delete(_url: string, params?: HttpParams): Observable<any> {
    const url = this.baseUrl + _url;
    return this.http
      .delete(url, {
        headers: this.commonHeaders,
        params: params,
        responseType: 'json',
        withCredentials: true,
      })
      .pipe(catchError(this.handleError));
  }

  public download(_url: string, params?: HttpParams): Observable<any> {
    const url = this.baseUrl + _url;
    return this.http
      .get(url, {
        headers: this.commonHeaders,
        observe: 'response',
        params: params,
        responseType: 'blob',
        withCredentials: true,
      })
      .pipe(catchError(this.handleError));
  }

  public openInBrowserFromAWSUrl(_url: string): void {
    let contentType;
    let contentDisposition;
    fetch(_url)
      .then(r => {
        contentType = r.headers.get('Content-type');
        contentDisposition = r.headers.get('Content-Disposition');
        return r.blob();
      })
      .then(blob => {
        const fileName = contentDisposition.match(/"(.*?)"/)[1];
        const filePart = fileName.split('.');
        const fileExtension = filePart[filePart.length - 1].toLowerCase();

        const newBlob = new Blob([blob], { type: `application/${fileExtension}` });

        // IE doesn't allow using a blob object directly as link href
        // instead it is necessary to use msSaveOrOpenBlob
        // if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        //   window.navigator.msSaveOrOpenBlob(newBlob);
        //   return;
        // }

        // For other browsers:
        // Create a link pointing to the ObjectURL containing the blob.
        const data = window.URL.createObjectURL(newBlob);

        // If the format is compatible with a browser, open in new tab
        if (fileExtension === 'pdf' || fileExtension === 'xml' || fileExtension === 'json') {
          window.open(data);
          setTimeout(() => window.URL.revokeObjectURL(data), 100);
          return;
        }

        // Otherwise download as attachment
        const link = document.createElement('a');
        link.href = data;
        link.download = fileName;
        link.click();
      });
  }

  public downloadAndSaveFile(
    _url: string,
    params?: HttpParams,
    filename?: string,
  ): Observable<void> {
    const url = this.baseUrl + _url;
    return this.http
      .get(url, {
        headers: this.commonHeaders,
        observe: 'response',
        params: params,
        responseType: 'blob',
        withCredentials: true,
      })
      .pipe(
        map(res => {
          filename = filename || 'file';
          const disposition =
            res.headers.get('content-disposition') || res.headers.get('Content-Disposition');
          if (disposition && disposition.indexOf('attachment') !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
              filename = matches[1].replace(/['"]/g, '');
            }
          }

          const obj = window.URL.createObjectURL(new Blob([res.body], { type: res.body.type }));
          const link = window.document.createElement('a');
          link.download = filename;
          link.href = obj;
          link.click();
        }),
        catchError(this.handleError),
      );
  }

  private handleError(error): Observable<any> {
    return observableThrowError(new FlamingoError(error));
  }
}
